import React, { useState } from 'react';
import { usePlayer } from '../contexts/PlayerContext';
import styled from 'styled-components';
import Visualizer from './Visualizer';



const PlayerContainer = styled.div<{ show: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 12px 16px;
  display: ${props => props.show ? 'flex' : 'none'};
  flex-direction: column;
  z-index: 1000;
  
  @media (min-width: 769px) {
    flex-direction: row;
    align-items: center;
    gap: 16px;
    height: 72px;
  }
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    gap: 8px;
  }
`;

const SongInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 180px;
  flex: 0 1 30%;
  
  @media (max-width: 768px) {
    min-width: 120px;
    flex: 1;
  }
`;

const SongCover = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }
`;

const SongDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const SongName = styled.div`
  font-weight: 400;
  margin-bottom: 2px;
  font-size: 0.9rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const SongArtist = styled.div`
  color: var(--text-muted);
  font-size: 0.75rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  
  @media (max-width: 768px) {
    gap: 4px;
  }
`;

const ControlButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 18px;
  padding: 6px;
  border-radius: 50%;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    padding: 4px;
  }
`;

const PlayButton = styled(ControlButton)`
  background: var(--primary-color);
  font-size: 20px;
  width: 40px;
  height: 40px;
  
  &:hover {
    background: var(--secondary-color);
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    font-size: 16px;
  }
`;

const ProgressSection = styled.div`
  flex: 1;
  max-width: 300px;
  margin: 0 16px;
  
  @media (max-width: 768px) {
    margin: 0;
    max-width: none;
    order: -1;
    width: 100%;
  }
`;

const MobileProgressSection = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    width: 100%;
    order: -1;
    margin-bottom: 8px;
  }
`;

const PlayerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  
  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  margin: 8px 0;
  cursor: pointer;
  position: relative;
  
  @media (max-width: 768px) {
    height: 4px;
    margin: 4px 0;
  }
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
  border-radius: 3px;
  width: ${props => props.progress}%;
  transition: width 0.1s ease;
`;

const TimeDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: var(--text-muted);
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
  }
`;

const VolumeSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 100px;
  flex: 0 1 30%;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    min-width: 60px;
    flex: 0 1 auto;
  }
`;

const VolumeSlider = styled.input`
  width: 80px;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  outline: none;
  cursor: pointer;
  
  &::-webkit-slider-thumb {
    appearance: none;
    width: 12px;
    height: 12px;
    background: var(--primary-color);
    border-radius: 50%;
    cursor: pointer;
  }
  
  @media (max-width: 768px) {
    width: 60px;
  }
`;

const Player: React.FC = () => {
  const {
    currentSong,
    isPlaying,
    currentTime,
    duration,
    volume,
    playlist,
    currentIndex,
    playSong,
    pauseSong,
    resumeSong,
    nextSong,
    previousSong,
    seekTo,
    setVolume,
    audioRef
  } = usePlayer();

  const [isMuted, setIsMuted] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(1);



  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const newTime = (clickX / width) * duration;
    seekTo(newTime);
  };

  const toggleMute = () => {
    if (isMuted) {
      setVolume(previousVolume);
      setIsMuted(false);
    } else {
      setPreviousVolume(volume);
      setVolume(0);
      setIsMuted(true);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  if (!currentSong) {
    return null;
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <>
      <audio ref={audioRef} />
      <PlayerContainer show={!!currentSong}>
        {/* Barra de progresso mobile */}
        <MobileProgressSection>
          <ProgressBar onClick={handleProgressClick}>
            <ProgressFill progress={progress} />
          </ProgressBar>
          <TimeDisplay>
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </TimeDisplay>
        </MobileProgressSection>
        
        {/* Conteúdo principal do player */}
        <PlayerContent>
          <SongInfo>
            <SongCover>
              🎵
            </SongCover>
            <SongDetails>
              <SongName>{currentSong.name}</SongName>
              <SongArtist>Soulful Roots</SongArtist>
            </SongDetails>
          </SongInfo>

          <Controls>
            <ControlButton 
              onClick={previousSong}
              disabled={currentIndex === 0}
            >
              ⏮️
            </ControlButton>
            
            <PlayButton onClick={() => {
              if (isPlaying) {
                pauseSong();
              } else if (currentSong) {
                if (audioRef.current && audioRef.current.src) {
                  resumeSong();
                } else {
                  // Re-inicializar se necessário
                  playSong(currentSong, playlist);
                }
              }
            }}>
              {isPlaying ? '⏸️' : '▶️'}
            </PlayButton>
            
            <ControlButton 
              onClick={nextSong}
              disabled={currentIndex === playlist.length - 1}
            >
              ⏭️
            </ControlButton>
          </Controls>

          {/* Barra de progresso desktop */}
          <ProgressSection>
            <Visualizer />
            <ProgressBar onClick={handleProgressClick}>
              <ProgressFill progress={progress} />
            </ProgressBar>
            <TimeDisplay>
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </TimeDisplay>
          </ProgressSection>

          <VolumeSection>
            <VolumeSlider
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={handleVolumeChange}
            />
          </VolumeSection>
        </PlayerContent>
      </PlayerContainer>
    </>
  );
};

export default Player;